import React, { useContext, useState } from "react";
import { Button, Menu, Dropdown, Modal, notification } from "antd";
import { SecurityContext } from "context/SecurityContextProvider";
import {
  QuestionCircleOutlined,
  SyncOutlined,
  MailOutlined,
  UnorderedListOutlined,
  CaretDownOutlined,
  DownloadOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { SeguimientoService } from "services/SeguimientoService";
import { useAtom } from "jotai";
import { useParams } from "react-router-dom";
import {
  datosPaginacionAtom,
  detalleEnvioSeguimientoAtom,
  documentosConErrorAtom,
  documentosSeleccionadoAtom,
} from "../../ListarDetalleSeguimientoStore";

import { DocumentoLaboralService } from "services/DocumentoLaboralService";
import { EnvioService } from "services/EnvioService";
import { FormatoCarga } from "enums/FormatoCarga";
import { EstadoDocumento } from "enums/EstadoDocumento";
import { saveAs } from "file-saver";

const BotonesGlobales = ({
  setResumenData,
  categoria,
  tipoDocumentoId,
  tipoDocumentoNombre,
  setReset,
  pendienteAprobacion,
  formatoCargaId,
}) => {
  const { getUsuarioAutenticado } = useContext(SecurityContext);
  const { envioId } = useParams();
  const isSDF = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20330791684";
  const isSDFEnergia = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20512436090";
  const [documentosSeleccionados, setDocumentosSeleccionados] = useAtom(documentosSeleccionadoAtom);
  const [documentosConError] = useAtom(documentosConErrorAtom);
  const [detalleEnvio, setDetalleEnvio] = useAtom(detalleEnvioSeguimientoAtom);
  const [, setDatosPaginacion] = useAtom(datosPaginacionAtom);

  const onClickBtnRefrescar = async () => {
    setReset(true);
    const detalleEnvios = await DocumentoLaboralService.listar(
      getUsuarioAutenticado().empresaSeleccionada.id,
      envioId,
      null,
      null,
      null,
      null,
      getUsuarioAutenticado().login,
      true
    );
    setDetalleEnvio(detalleEnvios.detalles);
    setResumenData(detalleEnvios);
    setReset(false);
    setDatosPaginacion({ paginaActual: 1, registrosPorPagina: 30 });
  };

  const mostralModalNotificarCorreoElectronicoMasiva = () => {
    Modal.confirm({
      bodyStyle: { display: "flex", flexDirection: "column" },
      icon: <QuestionCircleOutlined style={{ fontSize: "32px" }} />,
      content:
        documentosSeleccionados.length > 0 ? (
          <span style={{ textAlign: "center", display: "block", fontSize: "16px" }}>
            ¿Está seguro de <b>reenviar la notificación</b> de los (<b>{documentosSeleccionados?.length}</b>) documentos
            seleccionados?
          </span>
        ) : (
          <span style={{ textAlign: "center", display: "block", fontSize: "16px" }}>
            ¿Está seguro de <b>reenviar la notificación</b> de los (<b>{documentosConError?.length}</b>) documentos con
            error?
          </span>
        ),
      autoFocusButton: "cancel",
      centered: true,
      maskClosable: true,
      cancelText: "No, cerrar ventana",
      okText: "Si, notificar documentos",
      onOk: async () => {
        try {
          setDocumentosSeleccionados([]);
          await SeguimientoService.reenviarNotificacionMasivamente(
            getUsuarioAutenticado().organizacionId,
            getUsuarioAutenticado().empresaSeleccionada.id,
            envioId,
            documentosSeleccionados.length > 0 ? documentosSeleccionados : documentosConError,
            categoria,
            tipoDocumentoId,
            tipoDocumentoNombre
          );

          notification.success({
            message: `Notificación`,
            description: "Se procedio a realizar las notificaciones.",
            placement: "topRight",
          });
        } catch (error) {
          setDocumentosSeleccionados([]);
          console.error(error);
        }
      },
      afterClose: () => {
        setDocumentosSeleccionados([]);
      },
    });
  };

  const mostrarModalReprocesar = () => {
    Modal.confirm({
      bodyStyle: { display: "flex", flexDirection: "column" },
      icon: <QuestionCircleOutlined style={{ fontSize: "32px" }} />,
      content: (
        <span style={{ textAlign: "center", display: "block", fontSize: "16px" }}>
          ¿Está seguro de <b>reprocesar</b> los documentos seleccionados?
        </span>
      ),
      autoFocusButton: "cancel",
      centered: true,
      maskClosable: true,
      cancelText: "No, cerrar ventana",
      okText: "Si, reprocesar documentos",
      onOk: async () => {
        try {
          await SeguimientoService.reenviarNotificacionMasivamente(
            getUsuarioAutenticado().organizacionId,
            getUsuarioAutenticado().empresaSeleccionada.id,
            envioId,
            documentosConError,
            categoria,
            tipoDocumentoId,
            tipoDocumentoNombre
          );

          notification.success({
            message: `Notificación`,
            description: "Se procedio a reprocesar las notificaciones masivamente.",
            placement: "topRight",
          });
        } catch (error) {
          console.error(error);
        }
      },
    });
  };

  const mostrarModalReprocesarExcel = () => {
    if (documentosSeleccionados.length === 0) return;

    const idsParaReprocesar = documentosSeleccionados.filter((id) => {
      const doc = detalleEnvio.find((d) => d.id === id);
      return doc && doc.estadoDocumento !== EstadoDocumento.GENERADO;
    });
    const ignorados = documentosSeleccionados.length - idsParaReprocesar.length;

    if (idsParaReprocesar.length === 0) {
      notification.warning({
        message: "Sin documentos a reprocesar",
        description: "Todos los documentos seleccionados ya están en estado GENERADO y no requieren reprocesamiento.",
        placement: "topRight",
      });
      return;
    }

    Modal.confirm({
      bodyStyle: { display: "flex", flexDirection: "column" },
      icon: <QuestionCircleOutlined style={{ fontSize: "32px" }} />,
      content: (
        <span style={{ textAlign: "center", display: "block", fontSize: "16px" }}>
          ¿Está seguro de <b>reprocesar</b> los (<b>{idsParaReprocesar.length}</b>) documentos pendientes seleccionados?
          {ignorados > 0 && (
            <><br /><span style={{ fontSize: "13px", color: "#888" }}>({ignorados} ya están GENERADO y serán ignorados)</span></>
          )}
        </span>
      ),
      autoFocusButton: "cancel",
      centered: true,
      maskClosable: true,
      cancelText: "No, cerrar ventana",
      okText: "Sí, reprocesar",
      onOk: async () => {
        let exitosos = 0;
        let fallidos = 0;
        for (const detalleEnvioId of idsParaReprocesar) {
          try {
            await EnvioService.reprocesarDetalleExcel(detalleEnvioId);
            exitosos++;
          } catch (error) {
            console.error("Error al reprocesar detalleEnvioId: " + detalleEnvioId, error);
            fallidos++;
          }
        }
        setDocumentosSeleccionados([]);
        if (fallidos === 0) {
          notification.success({
            message: "Reprocesamiento",
            description: `Se reprocesaron exitosamente ${exitosos} documento(s).`,
            placement: "topRight",
          });
        } else {
          notification.warning({
            message: "Reprocesamiento parcial",
            description: `${exitosos} documento(s) reprocesados, ${fallidos} con error. Revise los logs del servidor.`,
            placement: "topRight",
          });
        }
      },
      afterClose: () => {
        setDocumentosSeleccionados([]);
      },
    });
  };

  const onClickMenuAcciones = (opcion) => {
    if (opcion.key === "reenviar-correo-masivo") {
      mostralModalNotificarCorreoElectronicoMasiva();
    } else if (opcion.key === "reprocesar-excel") {
      mostrarModalReprocesarExcel();
    } else if (opcion.key === "reprocesar") {
      mostrarModalReprocesar();
    } else if (opcion.key === "descargar-documentos-firmados") {
      EnvioService.descargarDocumentosFirmados(envioId)
        .then((response) => {
          saveAs(response.url, response.filename);
        })
        .catch((error) => {
          console.error("error al descargar documentos firmados", error);
          notification.error({
            message: "Error",
            description: "No se pudo descargar los documentos firmados.",
          });
        })
    } else if (opcion.key === "descargar-documentos-agrupados") {
      const notifKey = "descargar-agrupados";
      notification.open({
        key: notifKey,
        message: "Generando documento",
        description: "Por favor espere, se está generando el PDF agrupado...",
        icon: <LoadingOutlined style={{ color: "#1890ff" }} />,
        duration: 0,
      });
      EnvioService.descargarDocumentosAgrupados(envioId)
        .then((response) => {
          notification.success({
            key: notifKey,
            message: "Documento generado",
            description: "El PDF agrupado fue generado exitosamente.",
          });
          saveAs(response.url, response.filename);
        })
        .catch((error) => {
          notification.error({
            key: notifKey,
            message: "Error",
            description: "No se pudo generar el documento agrupado.",
          });
          console.error("error al descargar documentos agrupados..", error);
        });
    }
  };

  const menu = (
    <Menu onClick={onClickMenuAcciones}>
      <Menu.Item
        key="reenviar-correo-masivo"
        disabled={documentosConError?.length === 0 ? (documentosSeleccionados?.length === 0 ? true : false) : false}
      >
        <MailOutlined /> Reenviar Notificación
      </Menu.Item>
      {formatoCargaId === FormatoCarga.EXCEL.id && (
        <Menu.Item
          key="reprocesar-excel"
          disabled={documentosSeleccionados.length === 0}
        >
          <SyncOutlined /> Reprocesar Documento (Formato Excel)
        </Menu.Item>
      )}
      <Menu.Item key="descargar-documentos-firmados">
        <DownloadOutlined /> Descargar documentos firmados
      </Menu.Item>
      {(isSDF || isSDFEnergia) && (
        <Menu.Item key="descargar-documentos-agrupados">
          <DownloadOutlined /> Descargar documentos agrupados
        </Menu.Item>
      )}
    </Menu>
  );

  return (
    <React.Fragment>
      <Button type="link" onClick={onClickBtnRefrescar}>
        <SyncOutlined /> Refrescar
      </Button>

      {pendienteAprobacion && (
        <Dropdown overlay={menu} trigger={["click"]} arrow>
          <a className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
            <UnorderedListOutlined /> Acciones <CaretDownOutlined />
          </a>
        </Dropdown>
      )}
    </React.Fragment>
  );
};

export default BotonesGlobales;
